import React, { useCallback, useEffect, useMemo, useState } from 'react';
import posthog from 'posthog-js';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Input, InputNumber, Select, Spin, Popconfirm, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import {
  createEmptyTrip,
  createTrip,
  deleteTrip,
  getAccessibleTrip,
  tripWebHref,
  updateTrip,
} from '../../services/tripsService';
import { useCanonicalTripUrl } from '../../utils/useCanonicalTripUrl';
import { startPlanJobs } from '../../services/aiPlanJobService';
import type { Trip } from '../../types/client';
import { tripEditEn, tripDetailEn } from '../../content/trips.en';
import { resolveTripTemperatureUnit } from '../../utils/temperatureUnit';
import EditTripCityPanel from '../../components/EditTripCityPanel/EditTripCityPanel';
import { isPostHogConfigured } from '../../lib/posthog';
import {
  addCityToDraft,
  citiesNeedingAiReplan,
  cityRosterChanged,
  cloneTrip,
  defaultCityMeta,
  defaultPortMeta,
  defaultSeaDayMeta,
  deriveTripDatesFromCities,
  removedCityVisitIds,
  sortedCityIds,
  validateTripDraft,
} from '../../utils/tripEdit';
import styles from './TripEdit.module.css';

const { TextArea } = Input;

const TripEdit: React.FC = () => {
  const { tripId } = useParams<{ tripId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isDataSyncing } = useAuth();

  const isCreate = location.pathname === '/trips/new/edit' || tripId === 'new';

  const [original, setOriginal] = useState<Trip | null>(null);
  const [draft, setDraft] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useCanonicalTripUrl(isCreate ? null : original);

  const load = useCallback((): void => {
    if (!user?.sub) {
      setOriginal(null);
      setDraft(null);
      setLoading(false);
      return;
    }
    if (isCreate) {
      const state = location.state as { prefill?: Omit<Trip, 'id'> & { id?: string } } | null;
      const base = state?.prefill
        ? ({ ...createEmptyTrip(), ...state.prefill, id: 'new' } as Trip)
        : ({
            ...createEmptyTrip(),
            id: 'new',
            tripType: null,
            adults: 1,
            groupSize: 1,
          } as Trip);
      if (base.adults == null && base.groupSize == null) {
        base.adults = 1;
        base.groupSize = 1;
      }
      setOriginal(cloneTrip(base));
      setDraft(cloneTrip(base));
      setLoading(false);
      return;
    }
    if (!tripId) {
      setOriginal(null);
      setDraft(null);
      setLoading(false);
      return;
    }
    const trip = getAccessibleTrip(user.sub, tripId);
    if (!trip) {
      setOriginal(null);
      setDraft(null);
      setLoading(false);
      return;
    }
    const copy = cloneTrip(trip);
    if (copy.adults == null && copy.groupSize == null) {
      copy.adults = 1;
      copy.groupSize = 1;
    }
    setOriginal(cloneTrip(copy));
    setDraft(copy);
    setLoading(false);
  }, [user?.sub, tripId, isCreate, location.state]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isDataSyncing) load();
  }, [isDataSyncing, load]);

  const validationError = useMemo(
    () => (draft ? validateTripDraft(draft) : 'title'),
    [draft]
  );
  const canSave = Boolean(draft && !validationError && !saving);

  const handleCancel = (): void => {
    if (isCreate) navigate('/trips/new', { replace: true });
    else if (original && user?.sub) navigate(tripWebHref(user.sub, original), { replace: true });
    else if (tripId) navigate(`/trips/${tripId}`, { replace: true });
    else navigate('/trips', { replace: true });
  };

  const handleSave = async (): Promise<void> => {
    if (!user?.sub || !draft || !original) return;
    const err = validateTripDraft(draft);
    if (err === 'title') {
      message.error(tripEditEn.tripNameRequired);
      return;
    }
    if (err === 'cityName') {
      message.error(tripEditEn.cityNameRequired);
      return;
    }
    if (err === 'dates') {
      message.error(tripEditEn.validationDates);
      return;
    }
    if (err === 'overlap') {
      message.error(tripEditEn.validationOverlap);
      return;
    }

    setSaving(true);
    try {
      const dates = deriveTripDatesFromCities(draft);
      const travelers = Math.min(20, Math.max(1, draft.adults ?? draft.groupSize ?? 1));
      const prepared: Trip = {
        ...draft,
        title: draft.title.trim(),
        startDate: dates.startDate,
        endDate: dates.endDate,
        adults: travelers,
        groupSize: travelers,
        temperatureUnit: draft.temperatureUnit ?? resolveTripTemperatureUnit(draft),
      };

      if (isCreate) {
        const { id: _ignore, ...data } = prepared;
        const created = createTrip(user.sub, data);
        startPlanJobs(created);
        if (isPostHogConfigured) {
          posthog.capture('trip_created', {
            trip_type: created.tripType ?? 'unspecified',
            city_count: created.cityIds?.length ?? 0,
            traveler_count: created.adults ?? created.groupSize ?? 1,
          });
        }
        message.success(tripEditEn.created);
        navigate(tripWebHref(user.sub, created));
        return;
      }

      const removed = removedCityVisitIds(original, prepared);
      const updated = updateTrip(
        user.sub,
        prepared.id,
        {
          title: prepared.title,
          tripType: prepared.tripType,
          info: prepared.info,
          startDate: prepared.startDate,
          endDate: prepared.endDate,
          adults: prepared.adults,
          groupSize: prepared.groupSize,
          temperatureUnit: prepared.temperatureUnit,
          cityIds: prepared.cityIds,
          cityNames: prepared.cityNames,
          cityCoords: prepared.cityCoords,
          cityVisitMeta: prepared.cityVisitMeta,
          cityOptions: prepared.cityOptions,
          cityHistories: prepared.cityHistories,
          taskItems: prepared.taskItems,
        },
        { removedCityVisitIds: removed }
      );
      if (!updated) {
        message.error(tripEditEn.notFound);
        navigate('/trips');
        return;
      }

      const needing = citiesNeedingAiReplan(original, updated);
      if (needing.length > 0) {
        startPlanJobs(updated, { cityVisitIds: needing, clearExistingPlanData: true });
      }
      // Map thumb rebuilds from cityIds/cityCoords on My Trips (Static Maps URL).
      if (cityRosterChanged(original, updated)) {
        console.info('TripEdit: city roster changed — list map thumb will refresh from coords');
      }

      if (isPostHogConfigured) {
        posthog.capture('trip_updated', {
          trip_type: updated.tripType ?? 'unspecified',
          city_count: updated.cityIds?.length ?? 0,
          traveler_count: updated.adults ?? updated.groupSize ?? 1,
        });
      }
      message.success(tripEditEn.saved);
      navigate(tripWebHref(user.sub, updated));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (): void => {
    if (!user?.sub || !draft || isCreate) return;
    const didDelete = deleteTrip(user.sub, draft.id);
    if (!didDelete) {
      message.error(tripEditEn.notFound);
      return;
    }
    if (isPostHogConfigured) {
      posthog.capture('trip_deleted', {
        trip_type: draft.tripType ?? 'unspecified',
      });
    }
    message.success(tripEditEn.deleted);
    navigate('/trips');
  };

  const travelers = draft?.adults ?? draft?.groupSize ?? 1;
  const cityIds = draft ? sortedCityIds(draft) : [];
  const isCruise = draft?.tripType === 'cruise';

  if (loading || isDataSyncing) {
    return (
      <div className={styles.tripEdit}>
        <div className={styles.container}>
          <div className={styles.loading}>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className={styles.tripEdit}>
        <div className={styles.container}>
          <p>{tripEditEn.notFound}</p>
          <Button type="primary" onClick={() => navigate('/trips', { replace: true })}>
            {tripDetailEn.back}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tripEdit}>
      <div className={styles.container}>
        <div className={styles.header}>
          <Button type="link" className={styles.backLink} onClick={handleCancel}>
            <ArrowLeftOutlined /> {tripEditEn.cancel}
          </Button>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>
              {isCreate ? tripEditEn.newTripTitle : tripEditEn.editTripTitle}
            </h1>
            <Button
              type="primary"
              className={styles.saveTop}
              disabled={!canSave}
              loading={saving}
              onClick={() => void handleSave()}
            >
              {isCreate ? tripEditEn.createTrip : tripEditEn.saveChanges}
            </Button>
          </div>
        </div>

        <div className={styles.stack}>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{tripEditEn.sectionTripDetails}</h2>
          <div className={styles.field}>
            <label>{tripEditEn.tripName}</label>
            <Input
              value={draft.title}
              placeholder={tripEditEn.tripNamePlaceholder}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{tripEditEn.sectionTripInformation}</h2>
          <div className={styles.field}>
            <label>{tripEditEn.numberOfTravelers}</label>
            <InputNumber
              min={1}
              max={20}
              value={travelers}
              onChange={(v) => {
                const n = typeof v === 'number' ? v : 1;
                setDraft({ ...draft, adults: n, groupSize: n });
              }}
              style={{ width: '100%' }}
            />
          </div>
          <div className={styles.field}>
            <label>{tripEditEn.temperatureUnit}</label>
            <Select
              value={draft.temperatureUnit ?? resolveTripTemperatureUnit(draft)}
              onChange={(v) => setDraft({ ...draft, temperatureUnit: v })}
              options={[
                { value: 'F', label: tripEditEn.temperatureUnitF },
                { value: 'C', label: tripEditEn.temperatureUnitC },
              ]}
              style={{ width: '100%' }}
            />
          </div>
          <div className={styles.field}>
            <label>{tripEditEn.tripType}</label>
            <Select
              allowClear
              placeholder={tripEditEn.tripType}
              value={draft.tripType ?? undefined}
              onChange={(v) => setDraft({ ...draft, tripType: v ?? null })}
              options={Object.entries(tripDetailEn.tripTypeOptions).map(([value, label]) => ({
                value,
                label,
              }))}
              style={{ width: '100%' }}
            />
          </div>
          <div className={styles.field}>
            <label>{tripEditEn.description}</label>
            <TextArea
              rows={3}
              value={draft.info}
              placeholder={tripEditEn.descriptionPlaceholder}
              onChange={(e) => setDraft({ ...draft, info: e.target.value })}
            />
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{tripEditEn.sectionCities}</h2>
          {cityIds.length === 0 ? (
            <p className={styles.muted}>{tripEditEn.noCities}</p>
          ) : (
            <div className={styles.cityList}>
              {cityIds.map((id) => (
                <EditTripCityPanel key={id} trip={draft} cityId={id} onChange={setDraft} />
              ))}
            </div>
          )}
          <div className={styles.cityActions}>
            {isCruise ? (
              <>
                <Button
                  className={styles.cityActionBtn}
                  onClick={() =>
                    setDraft(
                      addCityToDraft(draft, '', defaultPortMeta(draft))
                    )
                  }
                >
                  {tripEditEn.addPort}
                </Button>
                <Button
                  className={styles.cityActionBtn}
                  onClick={() => setDraft(addCityToDraft(draft, 'At Sea', defaultSeaDayMeta(draft)))}
                >
                  {tripEditEn.addSeaDay}
                </Button>
              </>
            ) : (
              <Button
                className={styles.cityActionBtn}
                onClick={() =>
                  setDraft(
                    addCityToDraft(draft, '', defaultCityMeta(draft))
                  )
                }
              >
                {tripEditEn.addCity}
              </Button>
            )}
          </div>
        </section>

        <div className={styles.bottomActions}>
          <Button
            type="primary"
            className={styles.saveButton}
            disabled={!canSave}
            loading={saving}
            onClick={() => void handleSave()}
          >
            {isCreate ? tripEditEn.createTrip : tripEditEn.saveChanges}
          </Button>
          {!isCreate ? (
            <Popconfirm
              title={tripEditEn.deleteConfirm}
              okText={tripEditEn.deleteConfirmOk}
              okButtonProps={{ danger: true }}
              onConfirm={handleDelete}
            >
              <Button danger className={styles.deleteButton}>
                {tripEditEn.deleteTrip}
              </Button>
            </Popconfirm>
          ) : null}
        </div>
        </div>
      </div>
    </div>
  );
};

export default TripEdit;
